import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enUS from './locales/en-US.json';
import enGB from './locales/en-GB.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import it from './locales/it.json';
import pt from './locales/pt.json';
import ptBR from './locales/pt-BR.json';
import nl from './locales/nl.json';
import sv from './locales/sv.json';
import pl from './locales/pl.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import ar from './locales/ar.json';
import da from './locales/da.json';

/**
 * The scheduler connection diagram ships its own namespace.
 *
 * It is a separate namespace rather than more keys in `translation` because its keys are FLAT
 * strings containing dots ("node.scheduler", "node.scheduler.sub"). i18next's default key
 * separator is '.', so four of those pairs — node.orchestrator, node.scheduler, caption.repeat
 * and caption.recap, each of which is both a leaf AND a prefix — collide if they are nested into
 * a tree. Kept flat in their own namespace, i18next resolves them by exact match and nothing in
 * the existing `translation` tree is disturbed.
 *
 * All fifteen registered languages now carry the namespace, so nothing in the diagram falls back
 * to English while the rest of the screen is translated. en covers both en-US and en-GB; pt and
 * pt-BR have separate files. Every file holds the same 63 flat keys in the same order.
 */
import schedulerDiagramEn from './locales/schedulerDiagram/en.json';
import schedulerDiagramDe from './locales/schedulerDiagram/de.json';
import schedulerDiagramFr from './locales/schedulerDiagram/fr.json';
import schedulerDiagramEs from './locales/schedulerDiagram/es.json';
import schedulerDiagramIt from './locales/schedulerDiagram/it.json';
import schedulerDiagramPt from './locales/schedulerDiagram/pt.json';
import schedulerDiagramPtBR from './locales/schedulerDiagram/pt-BR.json';
import schedulerDiagramNl from './locales/schedulerDiagram/nl.json';
import schedulerDiagramSv from './locales/schedulerDiagram/sv.json';
import schedulerDiagramPl from './locales/schedulerDiagram/pl.json';
import schedulerDiagramJa from './locales/schedulerDiagram/ja.json';
import schedulerDiagramKo from './locales/schedulerDiagram/ko.json';
import schedulerDiagramAr from './locales/schedulerDiagram/ar.json';
import schedulerDiagramDa from './locales/schedulerDiagram/da.json';

const resources = {
    'en-US': {
        translation: enUS,
        schedulerDiagram: schedulerDiagramEn,
    },
    'en-GB': {
        translation: enGB,
        schedulerDiagram: schedulerDiagramEn,
    },
    es: {
        translation: es,
        schedulerDiagram: schedulerDiagramEs,
    },
    fr: {
        translation: fr,
        schedulerDiagram: schedulerDiagramFr,
    },
    de: {
        translation: de,
        schedulerDiagram: schedulerDiagramDe,
    },
    it: {
        translation: it,
        schedulerDiagram: schedulerDiagramIt,
    },
    pt: {
        translation: pt,
        schedulerDiagram: schedulerDiagramPt,
    },
    'pt-BR': {
        translation: ptBR,
        schedulerDiagram: schedulerDiagramPtBR,
    },
    nl: {
        translation: nl,
        schedulerDiagram: schedulerDiagramNl,
    },
    sv: {
        translation: sv,
        schedulerDiagram: schedulerDiagramSv,
    },
    pl: {
        translation: pl,
        schedulerDiagram: schedulerDiagramPl,
    },
    ja: {
        translation: ja,
        schedulerDiagram: schedulerDiagramJa,
    },
    ko: {
        translation: ko,
        schedulerDiagram: schedulerDiagramKo,
    },
    ar: {
        translation: ar,
        schedulerDiagram: schedulerDiagramAr,
    },
    da: {
        translation: da,
        schedulerDiagram: schedulerDiagramDa,
    },
};

// Check localStorage first to respect user's previous language selection
const getInitialLanguage = () => {
    const stored = localStorage.getItem('i18nextLng')
    if (stored) {
        return stored
    }
    // Check browser language as fallback
    const browserLang = navigator.language || navigator.languages?.[0] || 'en-US'
    // Map browser language to our supported languages
    if (browserLang.startsWith('en')) {
        if (browserLang === 'en-GB') return 'en-GB'
        // Map en, en-US, or any other en variant to en-US
        return 'en-US'
    }
    // Try to match the base language code
    const baseLang = browserLang.split('-')[0]
    const supportedLangs = ['es', 'fr', 'de', 'it', 'pt', 'nl', 'sv', 'pl', 'ja', 'ko', 'ar', 'da']
    if (supportedLangs.includes(baseLang)) {
        // Handle pt-BR specifically
        if (browserLang === 'pt-BR') return 'pt-BR'
        return baseLang
    }
    // Default to en-US if no match
    return 'en-US'
}

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        lng: getInitialLanguage(), // Use detected language instead of hardcoded en-US
        fallbackLng: ['en-US', 'en-GB'],
        // Unchanged for every existing call site — they all use a bare useTranslation().
        // Named namespaces (currently only 'schedulerDiagram') opt in explicitly.
        defaultNS: 'translation',
        debug: process.env.NODE_ENV === 'development',

        detection: {
            order: ['localStorage', 'navigator', 'htmlTag'],
            caches: ['localStorage'],
            lookupLocalStorage: 'i18nextLng',
        },

        interpolation: {
            escapeValue: false, // React already does escaping
        },
    });

export default i18n;





























